import { toAbsoluteUrl } from '@app/utils/toAbsoluteUrl';
import useSmartErrorHandling from '@hooks/useSmartErrorHandling';
import { Button } from 'antd';
import { memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface RunTimeErrorProps {
  error?: Error;
  resetError?: () => void;
}

const RunTimeError = ({ error, resetError }: RunTimeErrorProps = {}) => {
  const navigate = useNavigate();
  const { handleError: smartHandleError } = useSmartErrorHandling();
  const { t } = useTranslation();
  const [isUpdateError, setIsUpdateError] = useState(false);

  const handleRedirect = () => {
    if (resetError) {
      resetError(); // Reset error boundary if available
    }
    navigate('/');
  };

  const handleRetry = () => {
    if (resetError) {
      resetError(); // Reset error boundary if available
    } else {
      window.location.reload(); // Fallback to page reload
    }
  };

  useEffect(() => {
    if (error) {
      try {
        smartHandleError(error, {
          customTitle: 'Runtime Error',
          customMessage: 'An unexpected error occurred in the application',
          customType: 'Danger',
          forceShow: false, // Don't show notification since we're showing the error page
        });
      } catch {
        // Expected re-throw from handleError
      }
    }

    const handleGlobalError = (event: ErrorEvent) => {
      const errorMessage = event.message || '';

      // Check if error is due to a missing module or failed lazy load
      if (
        errorMessage.includes('Failed to fetch dynamically imported module') ||
        errorMessage.includes('Loading chunk') ||
        errorMessage.includes('Module not found')
      ) {
        setIsUpdateError(true);
      }
    };

    window.addEventListener('error', handleGlobalError);
    return () => {
      window.removeEventListener('error', handleGlobalError);
    };
  }, [error, smartHandleError]);
  return (
    <div className="bg-gray-700 w-screen h-screen flex justify-center flex-col items-center">
      <img
        data-testid="logoLg"
        alt="Logo"
        src={toAbsoluteUrl('media/logos/logo_dark.png')}
        className="ps-3 h-[75px] px-6"
      />
      {isUpdateError ? (
        <UpdateApp />
      ) : (
        <>
          <h1 className="fw-bolder fs-2hx text-gray-100 mb-4">{t('error-runtime-title')}</h1>
          <div className="fw-semibold fs-6 text-gray-300 mb-2">{t('error-runtime-description')}</div>
          <div className="fw-semibold fs-6 text-gray-300 mb-7">{t('error-runtime-reported')}</div>
          <div className="flex gap-3 justify-center">
            {resetError && <Button onClick={handleRetry}>{t('common-try-again')}</Button>}
            <Button type="primary" onClick={handleRedirect}>
              {t('common-back-to-dashboard')}
            </Button>
          </div>
        </>
      )}
    </div>
  );
};

const UpdateApp = () => {
  const { t } = useTranslation();
  const handleRefresh = () => {
    window.location.reload(); // Reload the app to get the latest version
  };
  return (
    <div className="text-center text-gray-100">
      <h2>⚠️ {t('error-update-required-title')}</h2>
      <p>{t('error-update-required-description')}</p>
      <Button type="primary" onClick={handleRefresh}>
        {t('common-reload-app')}
      </Button>
    </div>
  );
};

export default memo(RunTimeError);
